import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { EDIT_MODAL, AVAILABILITY } from "../lib/testIds";
import {
    fetchItemsMeta,
    updateListing,
    fetchItemAvailability,
    updateBlockedDates,
    uploadImage,
} from "../lib/api";
import { X, CalendarDays, Loader2, Upload, Camera, Image as ImageIcon } from "lucide-react";
import AvailabilityCalendar from "./AvailabilityCalendar";
import { PARKS, PARK_OTHER } from "../lib/parks";

const DEFAULT_CONDITIONS = ["new", "like-new", "good", "fair"];

export const EditListingModal = ({ item, open, onOpenChange, onSaved }) => {
    const [form, setForm] = useState({
        name: "",
        description: "",
        category: "",
        price_per_day: "",
        location: "",
        park: "",
        park_other: "",
        full_address: "",
        pickup_instructions: "",
        condition: "good",
        image_url: "",
    });
    const [categories, setCategories] = useState([]);
    const [conditions, setConditions] = useState(DEFAULT_CONDITIONS);
    const [saving, setSaving] = useState(false);

    // Availability state
    const [availability, setAvailability] = useState(null);
    const [blockedDates, setBlockedDates] = useState([]);
    const [availLoading, setAvailLoading] = useState(false);
    const [savingDates, setSavingDates] = useState(false);
    const [dirtyDates, setDirtyDates] = useState(false);

    // Image upload state
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    useEffect(() => {
        fetchItemsMeta()
            .then((m) => {
                setCategories(m.categories || []);
                setConditions(m.conditions || DEFAULT_CONDITIONS);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (item) {
            // If the item's saved park isn't in our canonical list, treat it
            // as an "Other" fill-in so the user can still see + edit it.
            const rawPark = item.park || "";
            const knownPark = PARKS.includes(rawPark) ? rawPark : "";
            const parkOther = !knownPark && rawPark ? rawPark : "";
            setForm({
                name: item.name || "",
                description: item.description || "",
                category: item.category || "",
                price_per_day: String(item.price_per_day ?? ""),
                location: item.location || "",
                park: knownPark || (rawPark ? PARK_OTHER : ""),
                park_other: parkOther,
                full_address: item.full_address || "",
                pickup_instructions: item.pickup_instructions || "",
                condition: item.condition || "good",
                image_url: item.image_url || "",
            });
        }
    }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open || !item?.id) return;
        setAvailLoading(true);
        setDirtyDates(false);
        fetchItemAvailability(item.id)
            .then((data) => {
                setAvailability(data);
                setBlockedDates(data.blocked_dates || []);
            })
            .catch(() => {})
            .finally(() => setAvailLoading(false));
    }, [open, item?.id]);

    if (!item) return null;

    const onChange = (field) => (e) => {
        setForm((f) => ({ ...f, [field]: e.target.value }));
    };

    const handlePhotoFile = async (file) => {
        if (!file) return;
        try {
            setUploadingPhoto(true);
            const data = await uploadImage(file);
            setForm((f) => ({ ...f, image_url: data.url }));
            toast.success("Photo updated", {
                description: "Hit Save changes to keep it.",
            });
        } catch (err) {
            toast.error("Couldn't upload photo", {
                description:
                    err?.response?.data?.detail ||
                    "Try a smaller JPG, PNG, or WEBP.",
            });
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleToggleDate = (iso, becameBlocked) => {
        setDirtyDates(true);
        setBlockedDates((prev) => {
            if (becameBlocked) {
                if (prev.includes(iso)) return prev;
                return [...prev, iso].sort();
            }
            return prev.filter((d) => d !== iso);
        });
    };

    const handleSaveDates = async () => {
        try {
            setSavingDates(true);
            const data = await updateBlockedDates(item.id, blockedDates);
            setAvailability(data);
            setBlockedDates(data.blocked_dates || []);
            setDirtyDates(false);
            toast.success("Availability saved", {
                description: `${data.blocked_dates.length} day${
                    data.blocked_dates.length === 1 ? "" : "s"
                } blocked.`,
            });
        } catch (err) {
            toast.error("Couldn't save availability", {
                description:
                    err?.response?.data?.detail ||
                    "Please try again in a moment.",
            });
        } finally {
            setSavingDates(false);
        }
    };

    const handleSave = async () => {
        const addressValue = form.full_address.trim();
        const pickupValue = form.pickup_instructions.trim();
        if (!addressValue) {
            toast.error("Full street address is required");
            return;
        }
        if (!pickupValue) {
            toast.error("Pickup instructions are required");
            return;
        }
        try {
            setSaving(true);
            const payload = {
                name: form.name.trim(),
                description: form.description.trim(),
                category: form.category,
                price_per_day: Number(form.price_per_day),
                location: form.location.trim() || null,
                park:
                    form.park === PARK_OTHER
                        ? form.park_other.trim() || null
                        : form.park || null,
                full_address: addressValue,
                pickup_instructions: pickupValue,
                condition: form.condition,
                image_url: form.image_url.trim(),
            };
            const saved = await updateListing(item.id, payload);
            toast.success("Listing updated", {
                description: `"${saved.name}" is saved.`,
            });
            onSaved?.(saved);
            onOpenChange(false);
        } catch (e) {
            toast.error("Couldn't save listing", {
                description: "Please double-check the fields and try again.",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={EDIT_MODAL.root}
                className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-0 text-[#2e3220]"
            >
                <DialogTitle className="sr-only">Edit {item.name}</DialogTitle>
                <DialogDescription className="sr-only">
                    Update listing details for {item.name}
                </DialogDescription>

                <div className="relative p-7 md:p-8">
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        data-testid={EDIT_MODAL.cancel}
                        aria-label="Close"
                        className="absolute right-5 top-5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#E8E2D0] text-[#2e3220] hover:bg-[#d5cfb8]"
                    >
                        <X className="h-4 w-4" strokeWidth={2.5} />
                    </button>

                    <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                        Edit listing
                    </p>
                    <h2 className="mt-1 font-display text-2xl font-bold text-[#2e3220] sm:text-3xl">
                        {form.name || "Untitled item"}
                    </h2>

                    <div className="mt-6 grid gap-4 md:grid-cols-2">
                        <label className="md:col-span-2 block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                Item name
                            </span>
                            <input
                                value={form.name}
                                onChange={onChange("name")}
                                data-testid={EDIT_MODAL.name}
                                className="mt-1 w-full rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                        </label>

                        <label className="md:col-span-2 block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                Description
                            </span>
                            <textarea
                                value={form.description}
                                onChange={onChange("description")}
                                data-testid={EDIT_MODAL.description}
                                rows={4}
                                className="mt-1 w-full resize-none rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm leading-relaxed text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                        </label>

                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                Category
                            </span>
                            <select
                                value={form.category}
                                onChange={onChange("category")}
                                data-testid={EDIT_MODAL.category}
                                className="mt-1 w-full appearance-none rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            >
                                {categories.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                Condition
                            </span>
                            <select
                                value={form.condition}
                                onChange={onChange("condition")}
                                data-testid={EDIT_MODAL.condition}
                                className="mt-1 w-full appearance-none rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            >
                                {conditions.map((c) => (
                                    <option key={c} value={c}>
                                        {c}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                Price per day ($)
                            </span>
                            <input
                                type="number"
                                min={0}
                                step="0.5"
                                value={form.price_per_day}
                                onChange={onChange("price_per_day")}
                                data-testid={EDIT_MODAL.price}
                                className="mt-1 w-full rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                        </label>

                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                Park / neighborhood
                            </span>
                            <select
                                value={form.park}
                                onChange={onChange("park")}
                                data-testid="edit-park"
                                className="mt-1 w-full rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            >
                                <option value="">Select a park…</option>
                                {PARKS.map((p) => (
                                    <option key={p} value={p}>
                                        {p}
                                    </option>
                                ))}
                                <option value={PARK_OTHER}>Other (fill in)</option>
                            </select>
                            {form.park === PARK_OTHER && (
                                <input
                                    value={form.park_other}
                                    onChange={onChange("park_other")}
                                    data-testid="edit-park-other"
                                    placeholder="Enter your park / community name"
                                    className="mt-2 w-full rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                                />
                            )}
                            <p className="mt-1 text-[10px] text-[#5a6142]">Public — shown on Browse.</p>
                        </label>

                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                Pickup location (fallback)
                            </span>
                            <input
                                value={form.location}
                                onChange={onChange("location")}
                                data-testid={EDIT_MODAL.location}
                                placeholder="Street name"
                                className="mt-1 w-full rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                        </label>

                        <label className="md:col-span-2 block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                Full street address <span className="text-[#B56C5A]">(required, private)</span>
                            </span>
                            <input
                                value={form.full_address}
                                onChange={onChange("full_address")}
                                data-testid="edit-full-address"
                                required
                                placeholder="1234 Maple St, Austin TX 78744"
                                className="mt-1 w-full rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                            <p className="mt-1 text-[10px] text-[#5a6142]">
                                Only revealed to a renter after they pay.
                            </p>
                        </label>

                        <label className="md:col-span-2 block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                Pickup instructions <span className="text-[#B56C5A]">(required)</span>
                            </span>
                            <textarea
                                rows={4}
                                value={form.pickup_instructions}
                                onChange={onChange("pickup_instructions")}
                                data-testid="edit-pickup-instructions"
                                required
                                placeholder="e.g. Side gate is unlocked between 9am–6pm — item is on the workbench."
                                className="mt-1 w-full resize-none rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm leading-relaxed text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                            <p className="mt-1 text-[10px] text-[#5a6142]">
                                Sent to the renter automatically once they pay.
                            </p>
                        </label>

                        <label className="md:col-span-2 block">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                Cover photo
                            </span>
                            <div className="mt-1 flex flex-col gap-3 rounded-2xl border border-[#2e3220]/15 bg-white/60 p-3 sm:flex-row sm:items-center">
                                <div className="relative h-24 w-24 flex-shrink-0 overflow-hidden rounded-xl bg-[#E8E2D0]">
                                    {form.image_url ? (
                                        <img
                                            src={form.image_url}
                                            alt="Listing"
                                            className="absolute inset-0 h-full w-full object-cover"
                                        />
                                    ) : (
                                        <div className="absolute inset-0 flex items-center justify-center text-[#737a3d]">
                                            <ImageIcon className="h-7 w-7" strokeWidth={2} />
                                        </div>
                                    )}
                                    {uploadingPhoto && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-[#F4F1EA]/80">
                                            <Loader2 className="h-5 w-5 animate-spin text-[#737a3d]" />
                                        </div>
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs text-[#5a6142]">
                                        JPG, PNG, or WEBP up to 8MB. The first photo is your cover.
                                    </p>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            disabled={uploadingPhoto}
                                            onClick={() => fileInputRef.current?.click()}
                                            data-testid="edit-photo-upload-btn"
                                            className="btn-primary !py-2 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <Upload className="h-3.5 w-3.5" strokeWidth={2.5} />
                                            Upload
                                        </button>
                                        <button
                                            type="button"
                                            disabled={uploadingPhoto}
                                            onClick={() => cameraInputRef.current?.click()}
                                            data-testid="edit-photo-camera-btn"
                                            className="btn-secondary !py-2 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <Camera className="h-3.5 w-3.5" strokeWidth={2.5} />
                                            Take photo
                                        </button>
                                        {form.image_url && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setForm((f) => ({ ...f, image_url: "" }))
                                                }
                                                data-testid="edit-photo-clear-btn"
                                                className="inline-flex items-center gap-1 rounded-full bg-[#2e3220]/5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#5a6142] hover:bg-[#2e3220]/10"
                                            >
                                                <X className="h-3 w-3" strokeWidth={2.5} />
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={(e) => {
                                        handlePhotoFile(e.target.files?.[0]);
                                        e.target.value = "";
                                    }}
                                    data-testid={EDIT_MODAL.image}
                                    className="hidden"
                                />
                                <input
                                    ref={cameraInputRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={(e) => {
                                        handlePhotoFile(e.target.files?.[0]);
                                        e.target.value = "";
                                    }}
                                    data-testid="edit-photo-camera-input"
                                    className="hidden"
                                />
                            </div>
                        </label>
                    </div>

                    {/* --- Availability calendar --- */}
                    <div className="mt-8 rounded-3xl border border-[#2e3220]/10 bg-[#E8E2D0]/40 p-5">
                        <div className="flex flex-wrap items-end justify-between gap-3">
                            <div>
                                <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                    <CalendarDays
                                        className="h-3.5 w-3.5"
                                        strokeWidth={2.5}
                                    />
                                    Availability
                                </p>
                                <h3 className="mt-1 font-display text-lg font-bold text-[#2e3220]">
                                    Block off dates you can&apos;t lend
                                </h3>
                                <p className="mt-1 max-w-md text-xs text-[#5a6142]">
                                    Tap a day to block it (vacation, maintenance, or
                                    personal use). Renters won&apos;t be able to book
                                    blocked days. Days with confirmed rentals are
                                    locked.
                                </p>
                            </div>
                            <p
                                data-testid={AVAILABILITY.blockedCount}
                                className="rounded-full bg-[#737a3d]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]"
                            >
                                {blockedDates.length} day
                                {blockedDates.length === 1 ? "" : "s"} blocked
                            </p>
                        </div>
                        {availLoading ? (
                            <div className="mt-4 h-72 animate-pulse rounded-3xl bg-[#E8E2D0]" />
                        ) : (
                            <div className="mt-4">
                                <AvailabilityCalendar
                                    mode="edit"
                                    availability={availability}
                                    blockedDates={blockedDates}
                                    onToggleDate={handleToggleDate}
                                />
                            </div>
                        )}
                        <div className="mt-4 flex justify-end">
                            <button
                                type="button"
                                disabled={!dirtyDates || savingDates}
                                onClick={handleSaveDates}
                                data-testid={AVAILABILITY.saveBtn}
                                className="btn-primary !py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {savingDates && (
                                    <Loader2
                                        className="h-3.5 w-3.5 animate-spin"
                                        strokeWidth={2.5}
                                    />
                                )}
                                {dirtyDates ? "Save availability" : "Saved"}
                            </button>
                        </div>
                    </div>

                    <div className="mt-7 flex flex-wrap justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="btn-secondary !py-2.5 text-sm"
                            data-testid={`${EDIT_MODAL.cancel}-foot`}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            data-testid={EDIT_MODAL.save}
                            className="btn-primary !py-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default EditListingModal;
